import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import type {
  ExternalLinkRel,
  ExternalPlacement,
  ExternalTarget,
  LinkMapPage,
} from '../../types';

/**
 * External links — partners, and sites the user runs outside Lumnian.
 *
 * NAMED FOR WHAT THEY ARE, not for their direction. These were "links out",
 * which collided with the edge pane's "Links out" heading for a page's own
 * outgoing INTERNAL links — the same two words for the two halves of the
 * feature, on screen at the same time. Internal vs external is the split every
 * SEO already thinks in.
 *
 * PLACEMENT IS DISTRIBUTION, NOT INFERENCE. "Put this on 50 of my pages" is a
 * different verb from "work out which of my pages should link to each other",
 * so this is a selection plus an apply, and Relink never touches what it makes.
 * That is also why it lives beside the graph rather than inside the Relink flow.
 *
 * `rel` IS PROMINENT ON PURPOSE. It is the one control in this whole feature
 * with consequences outside the app: a paid or reciprocal link that ships
 * without a declaration is a policy problem, not a style one. So it is a
 * visible field with a safe default rather than something folded into an
 * "advanced" section.
 */

const REL_OPTIONS: { value: ExternalLinkRel; label: string; hint: string }[] = [
  {
    value: 'SPONSORED',
    label: 'Sponsored',
    hint: 'Paid placements and partnerships. The safe default.',
  },
  {
    value: 'NOFOLLOW',
    label: 'Nofollow',
    hint: 'Pass no ranking credit.',
  },
  { value: 'UGC', label: 'User-generated', hint: 'Links from user content.' },
  {
    value: 'FOLLOW',
    label: 'Normal link',
    hint: 'Passes ranking credit. Only for links you are not paid for.',
  },
];

interface Props {
  mapId: number;
  /** Where each address already sits, so the modal can show and undo one
   *  placement instead of only offering to delete the address entirely. */
  placements: ExternalPlacement[];
  onUnplace: (placement: ExternalPlacement) => Promise<void>;
  /** Every page in the map — what "place on all" means. */
  pages: LinkMapPage[];
  onClose: () => void;
  onChanged: () => void;
}

export default function ExternalTargets({
  mapId,
  pages,
  placements,
  onUnplace,
  onClose,
  onChanged,
}: Props) {
  const [targets, setTargets] = useState<ExternalTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  /** Which address's placement list is open. One at a time: the lists can be
   *  long, and several open at once turns the modal into a wall. */
  const [openTarget, setOpenTarget] = useState<number | null>(null);

  const pageTitles = useMemo(
    () => new Map(pages.map((p) => [p.id, p.title])),
    [pages],
  );

  /**
   * Take an address off ONE page.
   *
   * The only removal that existed was deleting the address itself, which takes
   * it off every page at once — a much bigger act than "not this one", and the
   * only one the modal offered.
   */
  const unplaceOne = async (placement: ExternalPlacement) => {
    setBusy(true);
    try {
      await onUnplace(placement);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The address being edited, or null while adding a new one.
   *
   * ONE FORM, TWO JOBS. `PATCH /external-targets/{id}` existed from the start
   * and nothing ever called it: the `rel` you picked at creation was permanent,
   * which is the one field here with consequences outside the app. Fixing a
   * typo in a URL meant deleting the address and losing every placement with
   * it.
   */
  const [editingId, setEditingId] = useState<number | null>(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [anchor, setAnchor] = useState('');
  const [rel, setRel] = useState<ExternalLinkRel>('SPONSORED');

  const load = useCallback(async () => {
    try {
      setTargets(await api.listExternalTargets(mapId));
      setError('');
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not load your external links',
      );
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => {
    load();
  }, [load]);

  const blankForm = () => {
    setEditingId(null);
    setUrl('');
    setTitle('');
    setAnchor('');
    setRel('SPONSORED');
  };

  /** Load an address into the form above. Editing in place would mean a second
   *  copy of four inputs and the `rel` picker, which is the one control here
   *  that must not be allowed to drift between two renderings. */
  const edit = (t: ExternalTarget) => {
    setEditingId(t.id);
    setUrl(t.url);
    setTitle(t.title ?? '');
    setAnchor(t.default_anchor ?? '');
    setRel(t.rel);
    setNotice('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim() || busy) return;
    setBusy(true);
    try {
      const payload = {
        url: url.trim(),
        title: title.trim() || undefined,
        default_anchor: anchor.trim() || undefined,
        rel,
      };
      if (editingId != null) {
        await api.updateExternalTarget(mapId, editingId, payload);
        // Placements are untouched by a PATCH, so say so — "saved" alone
        // leaves you wondering whether the pages kept it.
        setNotice('Saved. The pages it is on are unchanged.');
      } else {
        await api.createExternalTarget(mapId, payload);
        setNotice('');
      }
      blankForm();
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that link');
    } finally {
      setBusy(false);
    }
  };

  const place = async (target: ExternalTarget, pageIds: number[], what: string) => {
    if (!pageIds.length || busy) return;
    setBusy(true);
    try {
      const result = await api.placeExternalTarget(mapId, target.id, {
        page_ids: pageIds,
      });
      // `already_present` is reported separately because selecting 50 pages and
      // placing 3 means the selection was mostly redundant, and one combined
      // number would hide that.
      setNotice(
        result.placed === 0
          ? `Already on ${what}.`
          : `Added to ${result.placed} page${result.placed === 1 ? '' : 's'}` +
            (result.already_present
              ? `; ${result.already_present} already had it.`
              : '.'),
      );
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not place that link');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Take an address off every page it is on — WITHOUT deleting the address.
   *
   * The pair to "Put on all". Deleting the address does this too, but it also
   * throws away the URL, the anchor and the `rel` you chose, so using it to
   * mean "start the placement again" costs you the thing you set up. These are
   * different acts and the modal now offers both.
   *
   * Confirmed, and the confirm counts: undoing it means re-picking the pages by
   * hand, which is the work this button just destroyed.
   */
  const unplaceAll = async (target: ExternalTarget, pageIds: number[]) => {
    if (!pageIds.length || busy) return;
    if (
      !window.confirm(
        `Take “${target.title || target.url}” off all ${pageIds.length} page` +
          `${pageIds.length === 1 ? '' : 's'}?\n\n` +
          'The address itself is kept, so you can place it again - but which ' +
          'pages had it is not remembered.\n\nContinue?',
      )
    )
      return;
    setBusy(true);
    try {
      const result = await api.unplaceExternalTarget(mapId, target.id, {
        page_ids: pageIds,
      });
      setNotice(
        `Removed from ${result.removed} page${result.removed === 1 ? '' : 's'}. ` +
          'The address is still here.',
      );
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that link');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (target: ExternalTarget) => {
    if (
      !window.confirm(
        `Delete this external link?\n\n` +
          `It will be removed from ${target.placement_count} page` +
          `${target.placement_count === 1 ? '' : 's'}.\n\nContinue?`,
      )
    )
      return;
    setBusy(true);
    try {
      await api.deleteExternalTarget(mapId, target.id);
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete that link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/70" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            External links
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Links to somewhere else - a partner, or a site of yours that isn’t in
            Lumnian. Add the address once, then put it on whichever pages you choose.
            Relink never touches these.
          </p>
        </div>

        {error && (
          <div className="mx-5 mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="mx-5 mt-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-3 py-2 rounded text-sm">
            {notice}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <form onSubmit={submit} className="space-y-2 pb-4 border-b border-gray-100 dark:border-gray-700">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://partner.example.com/page"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What is it? (for you)"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={anchor}
                onChange={(e) => setAnchor(e.target.value)}
                placeholder="Link text readers see"
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                How should search engines treat it?
              </label>
              <select
                value={rel}
                onChange={(e) => setRel(e.target.value as ExternalLinkRel)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {REL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {REL_OPTIONS.find((o) => o.value === rel)?.hint}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={!url.trim() || busy}
                className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {editingId != null ? 'Save changes' : 'Add link'}
              </button>
              {editingId != null && (
                <>
                  <button
                    type="button"
                    onClick={blankForm}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  {/* Says WHICH one, because the form is above the list and the
                      address being edited may have scrolled out of sight. */}
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    Editing “{targets.find((t) => t.id === editingId)?.title ||
                      targets.find((t) => t.id === editingId)?.url}”
                  </span>
                </>
              )}
            </div>
          </form>

          {loading ? (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          ) : targets.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              No external links yet.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {targets.map((t) => {
                const mine = placements.filter((x) => x.external_target_id === t.id);
                const has = new Set(mine.map((x) => x.page_id));
                const missing = pages.filter((p) => !has.has(p.id));
                return (
                <li
                  key={t.id}
                  className="border border-gray-200 dark:border-gray-700 rounded p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      {/* The way IN to editing. `PATCH` existed from the start
                          and nothing called it, so the `rel` chosen at creation
                          was permanent — the one field here with consequences
                          outside the app. */}
                      <button
                        onClick={() => edit(t)}
                        className={`text-left text-sm font-medium break-words hover:text-blue-600 dark:hover:text-blue-400 ${
                          editingId === t.id
                            ? 'text-blue-700 dark:text-blue-300 underline'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                        title="Edit this address"
                      >
                        {t.title || t.url}
                      </button>
                      <p className="text-xs font-mono text-gray-400 dark:text-gray-500 break-all">
                        {t.url}
                      </p>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        {/* Loud when it is FOLLOW, because that is the one
                            choice with consequences outside the app and the
                            one a reviewer needs to spot. */}
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            t.rel === 'FOLLOW'
                              ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {REL_OPTIONS.find((o) => o.value === t.rel)?.label ?? t.rel}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          on {t.placement_count} page
                          {t.placement_count === 1 ? '' : 's'}
                        </span>
                        {t.default_anchor && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            “{t.default_anchor}”
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => remove(t)}
                      disabled={busy}
                      className="shrink-0 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                      title="Delete this link and remove it everywhere"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {/* THE PAGES IT IS NOT ON. Counting all of them made the
                        button offer work it would not do: "Put on all 65 pages"
                        on an address already on 33 of them adds 32, and the
                        number you pressed was never the number that happened.
                        At zero it says so instead of pretending to be able. */}
                    <button
                      onClick={() =>
                        place(
                          t,
                          missing.map((p) => p.id),
                          'all of them',
                        )
                      }
                      disabled={busy || missing.length === 0}
                      title={
                        missing.length === 0
                          ? 'Every page in this map already has it'
                          : undefined
                      }
                      className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {missing.length === 0
                        ? 'On every page already'
                        : `Put on ${missing.length} more page${
                            missing.length === 1 ? '' : 's'
                          }`}
                    </button>
                    {mine.length > 0 && (
                      <button
                        onClick={() =>
                          unplaceAll(
                            t,
                            mine.map((x) => x.page_id),
                          )
                        }
                        disabled={busy}
                        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-900/30 dark:hover:text-red-300 disabled:opacity-40"
                        title="Take it off every page, but keep the address"
                      >
                        Remove from all {mine.length} page
                        {mine.length === 1 ? '' : 's'}
                      </button>
                    )}
                    {/* THE OTHER HALF, said rather than hidden. Bulk placement
                        is one button and a few pages is a gesture, and a modal
                        that only offers the bulk one reads as though bulk is
                        the only way. */}
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      or close this and drag it onto individual pages
                    </span>
                  </div>

                  {/* WHERE IT ALREADY IS, and how to take it off. There was no
                      way to see this at all — the count said "on 3 pages" and
                      the only removal available was deleting the address
                      everywhere, which is a much bigger act than "not this
                      one". */}
                  {mine.length > 0 && (
                    <div className="mt-2 border-t border-gray-100 dark:border-gray-700 pt-2">
                      <button
                        onClick={() =>
                          setOpenTarget((cur) => (cur === t.id ? null : t.id))
                        }
                        className="text-xs text-blue-700 dark:text-blue-300 hover:underline"
                        aria-expanded={openTarget === t.id}
                      >
                        {openTarget === t.id ? 'Hide' : 'Show'} the {mine.length}{' '}
                        page{mine.length === 1 ? '' : 's'} it is on
                      </button>
                      {openTarget === t.id && (
                        <ul className="mt-1.5 space-y-0.5">
                          {mine.map((x) => (
                            <li
                              key={x.id}
                              className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 group"
                            >
                              <span className="truncate">
                                {pageTitles.get(x.page_id) ?? `Page ${x.page_id}`}
                              </span>
                              <button
                                onClick={() => unplaceOne(x)}
                                disabled={busy}
                                className="icon-btn ml-auto shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40"
                                title="Take it off this page only"
                                aria-label={`Take this off ${
                                  pageTitles.get(x.page_id) ?? 'this page'
                                }`}
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
